import React, { useState } from 'react';
import { useDoctorQuery } from '../../hooks/useDoctorQuery';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import DoctorImage from './DoctorImage';
import NeedAuthModal from '../Modals/NeedAuthModal';
import GuestBookingModal from '../Modals/GuestInformationModal';
import AnimatedButton from '../Buttons/AnimatedButton';
import { useLanguage } from '../../context/LanguageContext';

export default function MainSection({ bookDisabled }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { data: doctorData } = useDoctorQuery();
    const { t,currentLanguage:language } = useLanguage();

    const isAuthorized = !!localStorage.getItem(`userTkn-${slug}`) || !!sessionStorage.getItem(`userTkn-${slug}`);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isGuestModalVisible, setIsGuestModalVisible] = useState(false);
    const [pendingRoute, setPendingRoute] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    if (!doctorData) return null;

    const doctorName = doctorData?.fullName || t('common.doctor');
    const specialization = doctorData?.specialization?.name;
    const photoId = doctorData?.photoId;
    const about = doctorData?.about || '';

    // Check if about text is longer than 300 characters
    const shouldShowSeeMore = about.length > 300;
    const truncatedAbout = shouldShowSeeMore && !isExpanded
        ? about.substring(0, 300) + '...'
        : about;

    const lines = truncatedAbout.split('\n').filter(line => line.trim() !== '');

    const containerVariants = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: 0.4,
            },
        },
    };

    const letterVariant = {
        hidden: { opacity: 0, y: 20 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: i * 0.05,
                duration: 0.4,
            },
        }),
    };

    const lineVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
    };

    const handleProtectedNavigation = (route) => {
        if (!isAuthorized) {
            setPendingRoute(route);
            setIsModalVisible(true);
        } else {
            navigate(route);
        }
    };

    const handleGuestBooking = () => {
        setIsModalVisible(false);
        setIsGuestModalVisible(true);
    };

    const handleGuestInfoSubmit = (guestInfo) => {
        setIsGuestModalVisible(false);
        sessionStorage.setItem(`guestInfo-${slug}`, JSON.stringify(guestInfo));
        sessionStorage.setItem(`isGuestBooking-${slug}`, 'true');
        navigate(pendingRoute || `/${slug}/book-appointment`);
    };

    const textFadeVariant = {
        hidden: { opacity: 0, y: 20 },
        visible: (i = 1) => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: i * 0.2,
                duration: 0.6,
                ease: 'easeOut',
            },
        }),
    };

    const rotateIn = {
        hidden: { opacity: 0, rotate: -10 },
        visible: (i = 1) => ({
            opacity: 1,
            rotate: 0,
            transition: { delay: i * 0.2, duration: 0.6 }
        })
    };

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <>
            <motion.div
                id='counter'
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className="bg-[#EBF8FA] rounded-md flex flex-col-reverse xl:flex-row xl:ps-[87px] py-5 "
            >
                <div className="flex-1 flex flex-col sm:gap-10 sm:p-10 py-3">
                    <div className="xl:text-start text-center">
                        <motion.h2
                            initial="hidden"
                            animate="visible"
                            style={{ display: 'inline-block', textAlign: 'center' }}
                            className="text-xl text-center md:text-5xl font-bold text-primary sm:mb-4 flex flex-wrap"
                        >
                            {`${t('common.dr')} ${doctorName}`.split("").map((char, index) => (
                                <motion.span
                                    key={index}
                                    variants={letterVariant}
                                    custom={index}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    {char === " " ? "\u00A0" : char}
                                </motion.span>
                            ))}
                        </motion.h2>

                        {specialization && (
                            <motion.p
                                custom={2}
                                variants={textFadeVariant}
                                initial="hidden"
                                animate="visible"
 className={`text-secondary flex gap-1 font-semibold mt-1 text-lg md:text-6xl ${
    language === 'ar' ? 'justify-end flex-row-reverse' : 'justify-start flex-row'
    }`}                            >
                                <span>{specialization}  </span> <span>{t('home.specialist')} </span>
                            </motion.p>
                        )}

                        {lines.length > 0 && (
                            <motion.div
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                                className="text-textPrimary md:text-lg sm:inline-block hidden mt-6"
                                aria-label={about}
                            >
                                <motion.div
                                    key={isExpanded ? 'expanded' : 'collapsed'}
                                    initial={{ opacity: 0, height: 'auto' }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {lines.map((line, idx) => (
                                        <motion.p
                                            key={idx}
                                            custom={idx + 3}
                                            variants={textFadeVariant}
                                            initial="hidden"
                                            animate="visible"
                                            className="mb-1"
                                        >
                                            {line}
                                        </motion.p>
                                    ))}
                                </motion.div>

                                {shouldShowSeeMore && (
                                    <motion.button
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.5 }}
                                        onClick={toggleExpanded}
                                        className="text-primary hover:text-primary_hover font-semibold mt-2 underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 rounded transition-colors duration-200"
                                        aria-label={isExpanded ? t('home.seeLess') : t('home.seeMore')}
                                    >
                                        {isExpanded ? t('home.seeLess') : t('home.seeMore')}
                                    </motion.button>
                                )}
                            </motion.div>
                        )}
                    </div>

                    <div className="mt-4 sm:mt-auto flex flex-col sm:flex-row sm:gap-5 gap-2 w-full sm:p-0 px-4">
                        <motion.div
                            custom={lines.length + 3}
                            variants={textFadeVariant}
                            initial="hidden"
                            animate="visible"
                            className="mt-4 sm:mt-auto flex flex-col sm:flex-row sm:gap-8 gap-2 w-full sm:p-0 px-4"
                        >
                            {doctorData?.features.includes("online_booking") &&
                                <AnimatedButton
                                    className={`w-full sm:px-5 sm:py-5 py-3 rounded-xl text- font-bold sm:text-4xl hover:scale-[1.02] transition-all duration-300`}
                                    primary
                                    disabled={bookDisabled || doctorData.isLocked}
                                    onClick={() => handleProtectedNavigation(`/${slug}/book-appointment`)}
                                >
                                    {t('home.getTreatment')}
                                </AnimatedButton>

                            }
                            <AnimatedButton
                                className={`sm:px-10 sm:py-5 py-3 rounded-xl  font-bold sm:text-xl hover:scale-[1.02] transition-all duration-300 ${bookDisabled ? 'bg-gray-400 cursor-default' : 'bg-primary hover:bg-primary_hover'}`}
                                secondary
                                onClick={() => handleProtectedNavigation(`/${slug}/my-appointments`)}
                            >
                                {t('home.appointments')}
                            </AnimatedButton>
                        </motion.div>
                    </div>
                </div>

                <DoctorImage photoId={photoId} />
            </motion.div>

            <NeedAuthModal
                open={isModalVisible}
                onConfirm={() => {
                    setIsModalVisible(false);
                    navigate(`/${slug}/auth`);
                }}
                onCancel={() => {
                    setIsModalVisible(false);
                    setPendingRoute(null);
                }}
                onGuestBooking={handleGuestBooking}
            />

            <GuestBookingModal
                open={isGuestModalVisible}
                onCancel={() => {
                    setIsGuestModalVisible(false);
                    setPendingRoute(null);
                }}
                onSubmit={handleGuestInfoSubmit}
            />
        </>
    );
}